// Captures `beforeinstallprompt` at module-load time so the event isn't lost
// before React mounts. Chrome can dispatch this during initial HTML parse,
// well before any useEffect runs. Importing this from `main.tsx` (or any
// module that loads with the app shell) hooks the listener as early as
// anything else in the bundle.
//
// Apps should import it for its side effect:
//
//     import '@ordo/ui/pwa-install-capture'

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: readonly string[]
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

let deferred: BeforeInstallPromptEvent | null = null
const listeners = new Set<(event: BeforeInstallPromptEvent | null) => void>()

function notify(event: BeforeInstallPromptEvent | null) {
  for (const listener of listeners) {
    listener(event)
  }
}

// `window` (not `globalThis`) is the right SSR guard. Node has globalThis
// but no addEventListener; React Server Components / Next.js evaluate this
// module on the server when imported transitively (e.g. via @ordo/ui's
// barrel from a Server Component file like RFP's app/error.tsx). Calling
// globalThis.addEventListener throws there.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferred = event as BeforeInstallPromptEvent
    notify(deferred)
  })
  window.addEventListener('appinstalled', () => {
    deferred = null
    notify(null)
  })
}

export function getDeferredPrompt(): BeforeInstallPromptEvent | null {
  return deferred
}

export function clearDeferredPrompt() {
  deferred = null
  notify(null)
}

export function subscribeDeferredPrompt(listener: (event: BeforeInstallPromptEvent | null) => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
